import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, CreditCard, Truck, Shield, Edit3, Award } from 'lucide-react';
import { SEO } from '../components/SEO';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const OrderPage = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [pageContent, setPageContent] = useState(null);

  useEffect(() => {
    fetchPageContent();
  }, []);

  const fetchPageContent = async () => {
    try {
      const response = await axios.get(`${API}/pages-content`);
      if (response.data && response.data.pages && response.data.pages.order) {
        setPageContent(response.data.pages.order);
      }
    } catch (error) {
      console.error('Error fetching page content:', error);
    }
  };

  // Хелпер для получения текста с фоллбэком
  const getText = (field, fallbackRu, fallbackLv) => {
    if (pageContent && pageContent[field]) {
      if (typeof pageContent[field] === 'object') {
        return pageContent[field][lang] || pageContent[field].ru || (lang === 'lv' ? fallbackLv : fallbackRu);
      }
      return pageContent[field];
    }
    return lang === 'lv' ? fallbackLv : fallbackRu;
  };

  // Получение массива из строки с переносами
  const getArrayFromText = (field, fallbackRu, fallbackLv) => {
    const text = getText(field, fallbackRu, fallbackLv);
    return text.split('\n').filter(item => item.trim());
  };

  const phone = "+37128448991";

  // Дефолтные тексты
  const defaults = {
    ru: {
      title: "Заказ и Осмотр",
      subtitle: "Процесс покупки в Pure Atelier Riga",
      engraving_title: "Персональная Гравировка",
      engraving_text: "Сделайте ваше кольцо по-настоящему особенным! Pure Atelier предлагает услугу гравировки любых инициалов, имён или дат на ювелирном изделии. Создайте уникальное послание любви, которое останется навсегда.",
      guarantee_title: "Гарантия Pure Atelier",
      guarantee_items: "Гарантия 12 месяцев на все изделия\nБесплатная профессиональная чистка раз в год\n30 дней на обмен\nСертификат подлинности для каждого бриллианта\nГарантия качества изготовления",
      process_title: "Процесс Покупки и Осмотра",
      step1_title: "1. Свяжитесь с нами",
      step1_text: "Позвоните по телефону +371 28 448 991, отправьте запрос на сайте или напишите в WhatsApp. Мы организуем индивидуальную консультацию в удобное для вас время.",
      step2_title: "2. Осмотр изделия",
      step2_text: "Встретьтесь с нашим экспертом, рассмотрите кольцо в деталях. Получите профессиональную консультацию по выбору.",
      step3_title: "3. Оплата",
      step3_text: "Мы принимаем оплату наличными или банковским переводом. Гибкие условия для вашего удобства.",
      step4_title: "4. Доставка",
      step4_text: "Бесплатная доставка на осмотр по всей Риге. При покупке - бесплатная доставка по всей Европе!",
      cta_button: "Записаться на осмотр",
      payment_cash: "Наличные",
      payment_transfer: "Банковский перевод",
      delivery_riga: "По всей Риге - бесплатная доставка для осмотра и покупки",
      delivery_europe: "По всей Европе - бесплатная доставка при покупке"
    },
    lv: {
      title: "Pasūtījums un Apskate",
      subtitle: "Pirkšanas process Pure Atelier Riga",
      engraving_title: "Personalizēta Gravēšana",
      engraving_text: "Padariet savu gredzenu patiesi īpašu! Pure Atelier piedāvā gravēšanas pakalpojumu ar jebkuriem iniciāļiem, vārdiem vai datumiem uz juvelierizstrādājuma. Izveidojiet unikālu mīlestības vēstījumu, kas paliks uz visiem laikiem.",
      guarantee_title: "Pure Atelier Garantija",
      guarantee_items: "Garantija 12 mēneši visiem izstrādājumiem\nBezmaksas profesionāla tīrīšana reizi gadā\n30 dienas apmaiņai\nAutentiskuma sertifikāts katram dimantam\nIzgatavošanas kvalitātes garantija",
      process_title: "Pirkšanas un Apskates Process",
      step1_title: "1. Sazinieties ar mums",
      step1_text: "Zvaniet pa tālruni +371 28 448 991, nosūtiet pieprasījumu vietnē vai rakstiet WhatsApp. Mēs organizēsim individuālu konsultāciju jums ērtā laikā.",
      step2_title: "2. Izstrādājuma apskate",
      step2_text: "Satiekat mūsu ekspertu, apskatiet gredzenu detalizēti. Saņemiet profesionālu konsultāciju par izvēli.",
      step3_title: "3. Maksājums",
      step3_text: "Mēs pieņemam maksājumu skaidrā naudā vai bankas pārskaitījumu. Elastīgi nosacījumi jūsu ērtībai.",
      step4_title: "4. Piegāde",
      step4_text: "Bezmaksas piegāde apskatei visā Rīgā. Pērkot - bezmaksas piegāde visā Eiropā!",
      cta_button: "Pierakstīties apskatei",
      payment_cash: "Skaidra nauda",
      payment_transfer: "Bankas pārskaitījums",
      delivery_riga: "Visā Rīgā - bezmaksas piegāde apskatei un pirkumam",
      delivery_europe: "Visā Eiropā - bezmaksas piegāde pērkot"
    }
  };

  const d = defaults[lang];

  return (
    <div className="min-h-screen pt-32 pb-24">
      <SEO
        title={getText('page_title', defaults.ru.title, defaults.lv.title)}
        description={getText('page_subtitle', defaults.ru.subtitle, defaults.lv.subtitle)}
      />

      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h1 className="font-heading text-5xl md:text-6xl mb-4 tracking-tight">
            {getText('page_title', defaults.ru.title, defaults.lv.title)}
          </h1>
          <p className="font-body text-xl text-muted-foreground">
            {getText('page_subtitle', defaults.ru.subtitle, defaults.lv.subtitle)}
          </p>
        </motion.div>

        {/* 1. Процесс покупки */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-16"
        >
          <h2 className="font-heading text-4xl mb-12 text-center tracking-tight">
            {getText('process_title', defaults.ru.process_title, defaults.lv.process_title)}
          </h2>
          
          <div className="space-y-8">
            {/* Шаг 1 */}
            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-full bg-accent text-accent-foreground flex items-center justify-center font-heading text-xl">
                  <Phone className="w-6 h-6" />
                </div>
              </div>
              <div>
                <h3 className="font-heading text-2xl mb-2 tracking-tight">
                  {getText('step1_title', defaults.ru.step1_title, defaults.lv.step1_title)}
                </h3>
                <p className="font-body text-lg text-muted-foreground leading-relaxed">
                  {getText('step1_text', defaults.ru.step1_text, defaults.lv.step1_text)}
                </p>
              </div>
            </div>

            {/* Шаг 2 */}
            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-full bg-accent text-accent-foreground flex items-center justify-center font-heading text-xl">
                  2
                </div>
              </div>
              <div>
                <h3 className="font-heading text-2xl mb-2 tracking-tight">
                  {getText('step2_title', defaults.ru.step2_title, defaults.lv.step2_title)}
                </h3>
                <p className="font-body text-lg text-muted-foreground leading-relaxed">
                  {getText('step2_text', defaults.ru.step2_text, defaults.lv.step2_text)}
                </p>
              </div>
            </div>

            {/* Шаг 3 */}
            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-full bg-accent text-accent-foreground flex items-center justify-center font-heading text-xl">
                  <CreditCard className="w-6 h-6" />
                </div>
              </div>
              <div>
                <h3 className="font-heading text-2xl mb-2 tracking-tight">
                  {getText('step3_title', defaults.ru.step3_title, defaults.lv.step3_title)}
                </h3>
                <p className="font-body text-lg text-muted-foreground leading-relaxed">
                  {getText('step3_text', defaults.ru.step3_text, defaults.lv.step3_text)}
                </p>
                <div className="mt-3 flex gap-4">
                  <span className="inline-block px-3 py-1 bg-background border border-border rounded text-sm">
                    {d.payment_cash}
                  </span>
                  <span className="inline-block px-3 py-1 bg-background border border-border rounded text-sm">
                    {d.payment_transfer}
                  </span>
                </div>
              </div>
            </div>

            {/* Шаг 4 */}
            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-full bg-accent text-accent-foreground flex items-center justify-center font-heading text-xl">
                  <Truck className="w-6 h-6" />
                </div>
              </div>
              <div>
                <h3 className="font-heading text-2xl mb-2 tracking-tight">
                  {getText('step4_title', defaults.ru.step4_title, defaults.lv.step4_title)}
                </h3>
                <p className="font-body text-lg text-muted-foreground leading-relaxed mb-3">
                  {getText('step4_text', defaults.ru.step4_text, defaults.lv.step4_text)}
                </p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-accent">
                    <Truck className="w-5 h-5" />
                    <span className="font-body">{d.delivery_riga}</span>
                  </div>
                  <div className="flex items-center gap-2 text-accent">
                    <Truck className="w-5 h-5" />
                    <span className="font-body">{d.delivery_europe}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* 2. Гравировка */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mb-16 p-8 bg-secondary rounded-lg"
        >
          <div className="flex items-start gap-4 mb-4">
            <Edit3 className="w-8 h-8 text-accent flex-shrink-0" />
            <h2 className="font-heading text-3xl tracking-tight">
              {getText('engraving_title', defaults.ru.engraving_title, defaults.lv.engraving_title)}
            </h2>
          </div>
          <p className="font-body text-lg leading-relaxed text-muted-foreground">
            {getText('engraving_text', defaults.ru.engraving_text, defaults.lv.engraving_text)}
          </p>
        </motion.section>

        {/* 3. Гарантии */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mb-16 p-8 bg-secondary rounded-lg"
        >
          <div className="flex items-start gap-4 mb-6">
            <Shield className="w-8 h-8 text-accent flex-shrink-0" />
            <h2 className="font-heading text-3xl tracking-tight">
              {getText('guarantee_title', defaults.ru.guarantee_title, defaults.lv.guarantee_title)}
            </h2>
          </div>
          <ul className="space-y-3">
            {getArrayFromText('guarantee_items', defaults.ru.guarantee_items, defaults.lv.guarantee_items).map((item, index) => (
              <li key={index} className="flex items-start gap-3">
                <Award className="w-5 h-5 text-accent flex-shrink-0 mt-1" />
                <span className="font-body text-lg text-muted-foreground">
                  {item}
                </span>
              </li>
            ))}
          </ul>
        </motion.section>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="text-center"
        >
          <Button
            size="lg"
            asChild
            className="bg-accent text-accent-foreground hover:bg-accent/90 text-lg px-8 py-6"
          >
            <a href={`tel:${phone}`}>
              <Phone className="w-5 h-5 mr-2" />
              {getText('cta_button', defaults.ru.cta_button, defaults.lv.cta_button)}
            </a>
          </Button>
          <p className="mt-4 font-body text-muted-foreground">
            {phone}
          </p>
        </motion.div>
      </div>
    </div>
  );
};
